import { store } from '../store'
import { getAuthService } from '@/auth/auth.service'
import Integrations from '@/services/integrations/RegisterIntegration'
import { clearStoredSideNavSettings } from '@/sideNav'

const TOKEN_KEYS = ['accesstoken', 'expiresin', 'refreshtoken']

export async function callback (handleRedirect) {
  const route = (await getAuthService().processCallback()).state

  if (process.env.USE_PHOTOPRINT === '1') {
    const tokenData = await fetchTokenData()

    await setupPhotoprintAuth(route, tokenData, handleRedirect)
  } else {
    await store.dispatch('authenticate')
    handleRedirect(route)
  }
}

async function setupPhotoprintAuth (route, tokenData, handleRedirect) {
  const res = await store.dispatch('user/saveToken', tokenData)

  if (res && res.Success) {
    if (res.PayLoad && res.PayLoad.IsRefreshRequired) {
      const successfulRefresh = await refreshToken()
      if (!successfulRefresh) {
        await clearUser()
        return
      }
    }

    const { type, code, store } = route.query

    const resRegister = await Integrations.registerIntegrationStore(type, code, store)
    if (resRegister && resRegister !== 'error') {
      console.error(resRegister)
    } else if (resRegister === 'error') {
      console.error(`Oops, there was an error registering your ${store}! Please visit Quick Setup in ShipBob to complete your installation!`)
    } else {
      handleRedirect(route)
    }
  } else {
    if (res && res.PayLoad && res.PayLoad.Message) {
      console.error(res.PayLoad.Message)
    }
    await clearUser()
  }
}

export async function login (route) {
  // clear all tokens
  localStorage.clear()
  // pass the Login source
  // powered by 3PL merchants land on Merchant App from their custom URL
  // https://XYZ-dev.shipbob.partners which then gets a (301) redirect to
  // https://webdev.shipbob.dev/app/Merchant/#/Login?source=xyz-dev.shipbob.partners
  // we care about the XYZ which will be used for cobranding the login page
  const source = route?.query?.source

  if (!source) {
    await getAuthService().login({ state: route })
    return
  }
  const loginSource = source.indexOf('.') !== -1 ? source.substring(0, source.indexOf('.')) : source
  // extract xyz from xyz-dev
  const loginSourceWithoutEnv = loginSource.indexOf('-') > -1 ? loginSource.substring(0, loginSource.indexOf('-')) : loginSource

  await getAuthService().login({
    state: route,
    extraQueryParams: {
      loginSource: loginSourceWithoutEnv
    }
  })
}

export async function logOut () {
  store.dispatch('user/qualifier/resetState')
  store.dispatch('dashboard/resetDashboard')
  const success = (process.env.USE_PHOTOPRINT === '1') ? await store.dispatch('user/clearAuth', null) : true

  if (success) {
    clearStoredSideNavSettings()
    removeAuth()
    const { isPoweredBySubAccountUser, resourceName } = store.getters['user/getExternalIdInfo']
    const args = {}
    if (isPoweredBySubAccountUser) {
      args.extraQueryParams = {
        logoutSource: resourceName
      }
    }
    await getAuthService().logout(args)
  }
}

export async function clearUser () {
  store.dispatch('user/qualifier/resetState')
  store.dispatch('dashboard/resetDashboard')
  const success = (process.env.USE_PHOTOPRINT === '1') ? await store.dispatch('user/clearAuth', null) : true

  if (success) {
    clearStoredSideNavSettings()
    removeAuth()
    await getAuthService().clearUser()
  }
}

const fetchTokenData = async () => {
  const authTokens = await getAuthService().getAllTokens()
  return mapAuth(authTokens)
}

export async function refreshToken () {
  const tokens = await fetchTokenData()
  if (!tokens.refreshToken) {
    return false
  }

  try {
    await getAuthService().renewToken()
  } catch (e) {
    return false
  }
  const tokenData = await fetchTokenData()
  const success = await store.dispatch('user/saveRefreshedToken', tokenData)
  store.dispatch('user/updateToken', tokens.accessToken)
  return success
}

export async function authenticate () {
  const tokens = await fetchTokenData()
  if (!tokens.accessToken || tokens.accessToken === '' ||
        Date.now() >= tokens.expiresIn * 1000) {
    return false
  }
  store.dispatch('user/updateToken', tokens.accessToken)
  return true
}

// needed by photoprint
export function persistAuthHeaders (headers) {
  if (!headers) return

  const hasAllTokens = TOKEN_KEYS.every(key => headers[key] !== undefined)
  if (!hasAllTokens) return

  const tokens = TOKEN_KEYS.reduce((acc, key) => {
    const header = headers[key]
    if (header) acc[key] = header
    return acc
  }, {})

  window.localStorage.setItem('auth', JSON.stringify(tokens))
}

function removeAuth () {
  window.localStorage.removeItem('auth')
  window.localStorage.removeItem('auth_oneIdClaim')
}

function mapAuth (authToken) {
  return {
    accessToken: authToken?.access_token,
    refreshToken: authToken?.refresh_token,
    expiresIn: authToken?.expires_at,
    idToken: authToken?.id_token,
    scope: authToken?.scope
  }
}
